import { supabase } from "@foundation/altzero_base/base/utils/supabaseClient";
import type {
  ApiKeyListItem,
  ApiKeyScope,
  CreateApiKeyInput,
  CreatedApiKey,
} from "../types";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
const ENDPOINT = `${API_URL}/api/developers/api-keys`;

const getAuthHeaders = async (): Promise<HeadersInit> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error("Not signed in");
  }
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${session.access_token}`,
  };
};

const parseListItem = (raw: Record<string, unknown>): ApiKeyListItem => ({
  id: String(raw.id),
  name: String(raw.name),
  expiresAt: new Date(raw.expiresAt as string),
  revokedAt: raw.revokedAt ? new Date(raw.revokedAt as string) : null,
  createdAt: new Date(raw.createdAt as string),
  isExpired: Boolean(raw.isExpired),
  isRevoked: Boolean(raw.isRevoked),
  userId: (raw.userId as string | null | undefined) ?? null,
  scope: (raw.scope as ApiKeyScope | undefined) ?? (raw.userId ? "personal" : "org"),
});

export const listApiKeys = async (
  scope: ApiKeyScope | "all" = "personal"
): Promise<ApiKeyListItem[]> => {
  const headers = await getAuthHeaders();
  const url = `${ENDPOINT}?scope=${encodeURIComponent(scope)}`;
  const response = await fetch(url, { method: "GET", headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to load API keys (${response.status})`);
  }

  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map(parseListItem);
};

export const createApiKey = async (
  input: CreateApiKeyInput
): Promise<CreatedApiKey> => {
  const headers = await getAuthHeaders();
  const response = await fetch(ENDPOINT, {
    method: "POST",
    headers,
    body: JSON.stringify({
      name: input.name,
      expiresAt: input.expiresAt.toISOString(),
      scope: input.scope,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to create API key (${response.status})`);
  }

  const raw = (await response.json()) as Record<string, unknown>;
  return {
    id: String(raw.id),
    name: String(raw.name),
    key: String(raw.key),
    expiresAt: new Date(raw.expiresAt as string),
    createdAt: new Date(raw.createdAt as string),
    userId: (raw.userId as string | null | undefined) ?? null,
    scope: (raw.scope as ApiKeyScope | undefined) ?? input.scope,
  };
};

export const revokeApiKey = async (id: string): Promise<void> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to revoke API key (${response.status})`);
  }
};
